import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Form, Input, Button, Alert, Card, Row, Col, Avatar, Typography, Divider } from 'antd';
import { UserOutlined, CameraOutlined } from '@ant-design/icons';
import ModernImageUpload from '../UI/ModernImageUpload';

const { Title, Text } = Typography;

function Profile() {
    const { user, updateProfile } = useAuth();
    const [form] = Form.useForm();
    const [initialized, setInitialized] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (user) {
            const values = {
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || '',
                profile_image: user.profile_image
            };
            form.setFieldsValue(values);
            setInitialized(true);
        }
    }, [user, form]);

    const onFinish = async (values) => {
        setLoading(true);
        setMessage('');
        setError('');
        try {
            const profileData = {
                name: values.name,
                email: values.email,
                phone: values.phone,
                profile_image: values.profile_image
            };

            const res = await updateProfile(profileData);
            if (res.success) {
                setMessage('Profile updated successfully');
                form.setFieldsValue(values);
            } else {
                setError(res.error || 'Update failed');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-orange-50/20 via-white to-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <Title level={2} className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent mb-2">
                        Your Profile
                    </Title>
                    <Text type="secondary" className="text-base text-slate-600">Manage your personal information and profile picture.</Text>
                </div>

                {message && <Alert type="success" message={message} className="mb-6 rounded-xl" closable />}
                {error && <Alert type="error" message={error} className="mb-6 rounded-xl" closable />}

                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={8}>
                        <Card className="shadow-lg border border-orange-100/50 modern-food-card" style={{ borderRadius: '20px' }}>
                            <div className="text-center">
                                <div className="mb-4">
                                    <Avatar 
                                        size={120} 
                                        src={user?.profile_image} 
                                        icon={<UserOutlined />}
                                        className="bg-gradient-to-br from-orange-100 to-orange-200 text-orange-600 border-4 border-white shadow-xl"
                                        style={{ fontSize: '48px' }}
                                    />
                                </div>
                                <Title level={4} className="!mb-2 font-bold text-gray-900">{user?.name}</Title>
                                <Text type="secondary" className="text-slate-600">{user?.email}</Text>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} lg={16}>
                        <Card className="shadow-lg border border-orange-100/50 modern-food-card" style={{ borderRadius: '20px' }}>
                        <Form 
                            layout="vertical" 
                            onFinish={onFinish} 
                            form={form}
                        >
                            <Form.Item 
                                label="Profile Picture" 
                                name="profile_image"
                            >
                                <ModernImageUpload
                                    currentImage={form.getFieldValue('profile_image')}
                                    onUpload={async (file) => {
                                        // For user profiles, we'll need to implement a user image upload endpoint
                                        // For now, just store the file object
                                        form.setFieldValue('profile_image', file);
                                        return { file_path: URL.createObjectURL(file) };
                                    }}
                                    onRemove={() => {
                                        form.setFieldValue('profile_image', null);
                                    }}
                                />
                            </Form.Item>

                            <Divider />

                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <Form.Item 
                                        label={<span className="font-semibold text-gray-700">Full Name</span>}
                                        name="name" 
                                        rules={[{ required: true, message: 'Please enter your name' }]}
                                    >
                                        <Input size="large" className="input-modern-vibrant h-12 rounded-xl" />
                                    </Form.Item>
                                </Col>
                                
                                <Col span={24}>
                                    <Form.Item 
                                        label={<span className="font-semibold text-gray-700">Email</span>}
                                        name="email" 
                                        rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
                                    >
                                        <Input size="large" className="input-modern-vibrant h-12 rounded-xl" />
                                    </Form.Item>
                                </Col>
                                
                                <Col span={24}>
                                    <Form.Item label={<span className="font-semibold text-gray-700">Phone</span>} name="phone">
                                        <Input size="large" className="input-modern-vibrant h-12 rounded-xl" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <div className="flex justify-end">
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    loading={loading}
                                    size="large"
                                    className="btn-vibrant px-8 h-12 text-base font-bold"
                                >
                                    Save Changes
                                </Button>
                            </div>
                        </Form>
                    </Card>
                </Col>
            </Row>
            </div>
        </div>
    );
}

export default Profile;
